<script setup lang="ts">import { computed as _computed } from 'vue';

import UserAvatarComponent from '@toolify/client/src/modules/entities/user/UserAvatarComponent.vue'

import {UserStatusType} from '@toolify/server/src/models/mongoose/UserModel/enum/UserStatusType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'

import {Placement} from '@floating-ui/vue'
import {
  IDatabaseTimeTrackerItemResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabaseTimeTrackerItemResponseInstance'


import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'


import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {getTimeTrackerItemName} from '@toolify/client/src/modules/timeTracker/functions/getTimeTrackerItemName'

interface Props {
  statusText?: string
  statusTextIcon?: IconNameType | null
  statusType?: UserStatusType
  portableProfilePlacement?: Placement
  entity: IResponseEntity
  activeDatabaseTimeTrackerItem?: IDatabaseTimeTrackerItemResponseInstance
}

const props = withDefaults(defineProps<Props>(), {
  statusType: null,
  portableProfilePlacement: 'left',
})

const nick = _computed(() => {
  return getEntityNameByEntity(props.entity)
})

const timeTrackingItemName = _computed(() => {
  return getTimeTrackerItemName(props.activeDatabaseTimeTrackerItem)
})
</script>

<template>
  <div class="entity-with-status" :class="[`status-${statusType}`]">
    <UserAvatarComponent
      alt=""
      statusBorderColor="hsl(0deg, 0%, 9%)"
      :statusType="statusType"
      :entity="entity"
    />
    <div class="details">
      <div class="nick">{{nick}}</div>
      <div class="description" v-if="statusText || statusTextIcon" :class="{'has-icon': statusTextIcon}">
        <IconComponent
          v-if="statusTextIcon"
          :name="statusTextIcon"
          :weightType="IconWeightType.Solid"
          :size="SizeVariantType.XSmall"
          :color="null"
          class="status-text-icon"
        />
        <div class="text" v-if="statusText">{{statusText}}</div>
        <div class="text" v-else>{{timeTrackingItemName}}</div>
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  .entity-with-status {
    border-radius: variables.$borderRadius;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--entity-status-gap);
    align-items: center;
    box-sizing: border-box;

    &.status-offline {
      opacity: 0.5;
    }
  }

  .user-avatar {
    height: map-get(variables.$sizes, size-lg);
  }

  .nick {
    font-weight: 600;
  }

  .description {
    font-size: map-get(variables.$textSizes, text-sm);
    color: map-get(variables.$colors, content-dark);
    display: grid;
    align-items: center;

    &.has-icon {
      grid-template-columns: auto 1fr;
      gap: var(--entity-status-description-gap);
    }
  }

  .status-text-icon {
    color: map-get(variables.$colors, primary);
  }

  .nick, .text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1rem;
  }

  .button {
    border-radius: variables.$borderRadius;
    aspect-ratio: 1/1;
    height: map-get(variables.$sizes, size-lg);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: hsl(0deg 0% 100% / 50%);

    &:hover {
      background: hsl(0deg 0% 100% / 10%);
      color: hsl(0deg 0% 100% / 85%);
    }
  }

  .details {
    display: grid;
    align-items: center;
  }
</style>
